import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { Icon } from '../design-system/icons';
import { borderRadius, type Side } from './graph-model';

/**
 * Rendu en couches partagé par l'explorateur du graphe (ADR-129) et l'atlas du modèle (ADR-130) : des
 * couloirs, des cartes et des liens courbes en SVG, aux jetons du design system. Aucune bibliothèque de
 * graphe ni second framework : les positions viennent de l'appelant. Cartes et libellés de liens sont des
 * boutons, atteignables au clavier ; `+`, `-` et `0` zooment, dézooment et ajustent.
 *
 * Défilement (remarque de l'utilisateur du 2026-10-15) : le schéma occupe la hauteur restante de la fenêtre
 * et ne fait pas défiler la page (`overscroll-behavior`) ; il se centre sur la carte `focus` (l'objet exploré)
 * à chaque changement ; le zoom garde fixe le point visé (Ctrl + molette au pointeur, boutons au centre) ;
 * un glisser sur le fond déplace la vue.
 */

export interface CanvasCard {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Côtés où des liens s'attachent : coins droits de ce côté. */
  sides: ReadonlySet<Side>;
  /** Teinte 1 à 8 du type (mini-carte). */
  tone: number;
  variant: 'center' | 'node' | 'collapsed' | 'more';
  dimmed: boolean;
  highlighted: boolean;
  /** Nom accessible du bouton. */
  label: string;
  /** Description accessible (gestes au clavier). */
  description?: string;
  children: ReactNode;
  /** Clic, ou Entrée au clavier. */
  onActivate?: () => void;
  /** Double clic, ou Maj+Entrée au clavier ; le clic simple attend alors d'être sûr qu'il n'y en a pas un second. */
  onDoubleActivate?: () => void;
  onFocus?: () => void;
}

export interface CanvasLink {
  key: string;
  d: string;
  dash?: string;
  width: number;
  dimmed: boolean;
  highlighted: boolean;
  arrow: boolean;
  label?: { text: string; x: number; y: number; badge?: string };
  selected: boolean;
  ariaLabel: string;
  onSelect?: () => void;
}

export interface CanvasLane {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  count?: string;
  /** `accent` : le couloir de l'objet exploré ; `header` : l'en-tête d'un groupe, sans fond. */
  variant?: 'accent' | 'header';
}

const MIN_SCALE = 0.3;
const MAX_SCALE = 2;
const CARD_RADIUS = 12;
/** Délai au-delà duquel un clic de souris n'est plus le premier d'un double clic. */
const DOUBLE_CLICK_MS = 250;
const clampScale = (value: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(value * 100) / 100));

export function GraphCanvas({
  width,
  height,
  lanes,
  cards,
  links,
  label,
  overlay,
  minimapLabel = 'Mini-carte',
  focus,
}: {
  width: number;
  height: number;
  lanes: readonly CanvasLane[];
  cards: readonly CanvasCard[];
  links: readonly CanvasLink[];
  label: string;
  overlay?: ReactNode;
  minimapLabel?: string;
  /** Carte sur laquelle centrer la vue à l'arrivée et à chaque changement ; sinon la carte `center`. */
  focus?: string;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [view, setView] = useState({ left: 0, top: 0, width: 0, height: 0 });
  // Point du monde à garder sous le même point de l'écran après un changement d'échelle.
  const anchor = useRef<{ x: number; y: number; screenX: number; screenY: number } | null>(null);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  // Clic simple en attente : un double clic l'annule, pour ne pas ouvrir la fiche avant de recentrer.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPending = (): void => {
    if (pending.current !== null) clearTimeout(pending.current);
    pending.current = null;
  };
  useEffect(() => cancelPending, []);
  const click = (card: CanvasCard, detail: number): void => {
    cancelPending();
    if (card.onActivate === undefined || detail > 1) return;
    // Au clavier (detail 0) ou sans double clic déclaré, l'action est immédiate.
    if (detail === 0 || card.onDoubleActivate === undefined) return card.onActivate();
    const activate = card.onActivate;
    pending.current = setTimeout(() => {
      pending.current = null;
      activate();
    }, DOUBLE_CLICK_MS);
  };

  const measure = useCallback(() => {
    const element = viewport.current;
    if (element === null) return;
    setView({ left: element.scrollLeft, top: element.scrollTop, width: element.clientWidth, height: element.clientHeight });
  }, []);
  // Hauteur : celle que la mise en page donne au schéma (`ay-graph--page`, `ay-graph--full`, graph.css),
  // jamais calculée ici d'après la hauteur du document, qui dépend du panneau voisin.
  useLayoutEffect(() => {
    const element = viewport.current;
    const target = anchor.current;
    if (element !== null && target !== null) {
      element.scrollLeft = Math.max(0, target.x * scale - target.screenX);
      element.scrollTop = Math.max(0, target.y * scale - target.screenY);
      anchor.current = null;
    }
    measure();
  }, [measure, scale, width, height]);
  useEffect(() => {
    // La zone visible change aussi sans redimensionner la fenêtre (panneau ouvert, plein écran) : mini-carte à jour.
    const element = viewport.current;
    const observer = typeof ResizeObserver === 'undefined' || element === null ? null : new ResizeObserver(measure);
    if (element !== null) observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  /** Change l'échelle en gardant fixe le point du schéma visé (`screenX`, `screenY`, relatifs à sa zone visible), le centre par défaut. */
  const zoomTo = useCallback((next: number, screenX?: number, screenY?: number) => {
    const element = viewport.current;
    const current = scaleRef.current;
    const target = clampScale(next);
    if (target === current) return;
    if (element !== null) {
      const sx = screenX ?? element.clientWidth / 2;
      const sy = screenY ?? element.clientHeight / 2;
      anchor.current = { x: (element.scrollLeft + sx) / current, y: (element.scrollTop + sy) / current, screenX: sx, screenY: sy };
    }
    setScale(target);
  }, []);
  const zoom = (factor: number): void => zoomTo(scaleRef.current * factor);
  const fit = (): void => {
    const element = viewport.current;
    if (element === null || element.clientWidth === 0 || element.clientHeight === 0) return setScale(1);
    anchor.current = { x: width / 2, y: height / 2, screenX: element.clientWidth / 2, screenY: element.clientHeight / 2 };
    setScale(clampScale(Math.min(1, element.clientWidth / width, element.clientHeight / height)));
  };

  // Centrage sur l'objet exploré : à l'arrivée et à chaque nouvel objet, pas à chaque rendu.
  const focusCard = cards.find((card) => (focus === undefined ? card.variant === 'center' : card.key === focus));
  const focusKey = focusCard?.key;
  useLayoutEffect(() => {
    const element = viewport.current;
    if (element === null || focusCard === undefined) return;
    const current = scaleRef.current;
    element.scrollLeft = Math.max(0, (focusCard.x + focusCard.w / 2) * current - element.clientWidth / 2);
    element.scrollTop = Math.max(0, (focusCard.y + focusCard.h / 2) * current - element.clientHeight / 2);
    measure();
    // Le centrage suit l'objet exploré, pas les autres rendus.
  }, [focusKey]);

  // Ctrl (ou Cmd) + molette : zoom au pointeur ; la molette seule fait défiler le schéma. Écouteur non passif pour retenir la page.
  useEffect(() => {
    const element = viewport.current;
    if (element === null) return;
    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const box = element.getBoundingClientRect();
      zoomTo(scaleRef.current * Math.exp(-event.deltaY * 0.002), event.clientX - box.left, event.clientY - box.top);
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  // Glisser sur le fond (pas sur une carte ni un lien) : déplace la vue.
  const drag = useRef<{ pointer: number; x: number; y: number; left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || event.pointerType === 'touch' || (event.target as Element).closest('button, a, input, select') !== null) return;
    const element = event.currentTarget;
    drag.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft, top: element.scrollTop };
    element.setPointerCapture?.(event.pointerId);
    setDragging(true);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const state = drag.current;
    if (state === null || state.pointer !== event.pointerId) return;
    event.currentTarget.scrollLeft = state.left - (event.clientX - state.x);
    event.currentTarget.scrollTop = state.top - (event.clientY - state.y);
  };
  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragging(false);
  };
  const onKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key === '+' || event.key === '=') zoom(1.2);
    else if (event.key === '-') zoom(1 / 1.2);
    else if (event.key === '0') fit();
    else return;
    event.preventDefault();
  };
  const jump = (x: number, y: number): void => {
    const element = viewport.current;
    if (element === null) return;
    element.scrollLeft = Math.max(0, x * scale - element.clientWidth / 2);
    element.scrollTop = Math.max(0, y * scale - element.clientHeight / 2);
    measure();
  };

  const world: CSSProperties = { width: width * scale, height: height * scale };
  const inner: CSSProperties = { width, height, transform: `scale(${scale})` };
  return (
    <div className="ay-graph__frame">
      <div
        ref={viewport}
        className={`ay-graph__viewport${dragging ? ' ay-graph__viewport--dragging' : ''}`}
        tabIndex={0}
        role="group"
        aria-label={label}
        aria-roledescription="schéma"
        aria-description="Molette : faire défiler. Ctrl + molette : zoomer. Glisser le fond : déplacer la vue."
        onKeyDown={onKey}
        onScroll={measure}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="ay-graph__world" style={world}>
          <div className="ay-graph__inner" style={inner}>
            {lanes.map((lane) => (
              <div key={lane.key} className={`ay-graph__lane${lane.variant === undefined ? '' : ` ay-graph__lane--${lane.variant}`}`} style={{ left: lane.x, top: lane.y, width: lane.w, height: lane.h }}>
                <span className="ay-graph__lanehead">
                  <span className="ay-graph__lanetitle" title={lane.title}>
                    {lane.title}
                  </span>
                  {lane.count !== undefined && <span className="ay-graph__lanecount">{lane.count}</span>}
                </span>
              </div>
            ))}
            <svg className="ay-graph__links" width={width} height={height} aria-hidden="true">
              <defs>
                <marker id="ay-graph-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0 0 L10 5 L0 10 z" className="ay-graph__arrowhead" />
                </marker>
                <marker id="ay-graph-arrow-on" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0 0 L10 5 L0 10 z" className="ay-graph__arrowhead ay-graph__arrowhead--on" />
                </marker>
              </defs>
              {links.map((link) => (
                <path
                  key={link.key}
                  d={link.d}
                  className={`ay-graph__link${link.highlighted || link.selected ? ' ay-graph__link--on' : ''}${link.dimmed ? ' ay-graph__link--dim' : ''}`}
                  strokeWidth={link.highlighted || link.selected ? link.width + 1.5 : link.width}
                  {...(link.dash === undefined ? {} : { strokeDasharray: link.dash })}
                  {...(link.arrow ? { markerEnd: `url(#${link.highlighted || link.selected ? 'ay-graph-arrow-on' : 'ay-graph-arrow'})` } : {})}
                />
              ))}
            </svg>
            {links.map((link) =>
              link.label === undefined ? null : (
                <button
                  key={`label:${link.key}`}
                  type="button"
                  className={`ay-graph__linklabel${link.highlighted || link.selected ? ' ay-graph__linklabel--on' : ''}${link.dimmed ? ' ay-graph__linklabel--dim' : ''}`}
                  style={{ left: link.label.x, top: link.label.y }}
                  aria-pressed={link.selected}
                  aria-label={link.ariaLabel}
                  onClick={link.onSelect}
                >
                  {link.label.text}
                  {link.label.badge !== undefined && <span className="ay-graph__linkbadge">{link.label.badge}</span>}
                </button>
              ),
            )}
            {cards.map((card) => (
              <button
                key={card.key}
                type="button"
                className={`ay-graph__card ay-graph__card--${card.variant}${card.highlighted ? ' ay-graph__card--on' : ''}${card.dimmed ? ' ay-graph__card--dim' : ''}`}
                style={{ left: card.x, top: card.y, width: card.w, height: card.h, borderRadius: borderRadius(card.sides, CARD_RADIUS) }}
                aria-label={card.label}
                {...(card.description === undefined ? {} : { 'aria-description': card.description })}
                data-key={card.key}
                onClick={(event) => click(card, event.detail)}
                onDoubleClick={() => {
                  cancelPending();
                  card.onDoubleActivate?.();
                }}
                onFocus={card.onFocus}
                onMouseEnter={card.onFocus}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && event.shiftKey && card.onDoubleActivate !== undefined) {
                    event.preventDefault();
                    card.onDoubleActivate();
                  }
                }}
              >
                {card.children}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="ay-graph__tools" role="group" aria-label="Zoom">
        <button type="button" aria-label="Zoomer" onClick={() => zoom(1.2)}>
          <Icon name="plus" size={16} />
        </button>
        <button type="button" aria-label="Dézoomer" onClick={() => zoom(1 / 1.2)}>
          <Icon name="minus" size={16} />
        </button>
        <button type="button" aria-label="Ajuster à l’écran" onClick={fit}>
          <Icon name="fit" size={16} />
        </button>
      </div>
      <svg
        className="ay-graph__minimap"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={minimapLabel}
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          if (box.width === 0 || box.height === 0) return;
          jump(((event.clientX - box.left) / box.width) * width, ((event.clientY - box.top) / box.height) * height);
        }}
      >
        {cards.map((card) => (
          <rect key={card.key} x={card.x} y={card.y} width={card.w} height={card.h} rx={6} className={`ay-graph__minicard ay-graph__tone--t${card.tone}`} />
        ))}
        {view.width > 0 && <rect className="ay-graph__miniview" x={view.left / scale} y={view.top / scale} width={view.width / scale} height={view.height / scale} />}
      </svg>
      {overlay}
    </div>
  );
}

/** Pastille d'un type : son icône dans sa couleur. */
export function TypeChip({ icon, tone, size = 30 }: { icon: string; tone: number; size?: number }) {
  return (
    <span className={`ay-graph__chip ay-graph__tone--t${tone}`} style={{ width: size, height: size }} aria-hidden="true">
      <Icon name={icon} size={Math.round(size * 0.55)} />
    </span>
  );
}
